// user-service.js
// Service for handling user-related operations

import axios from '@/axios-offline';

class UserService {
  // Get user listing with filters and pagination
  static async getUsers(params = {}) {
    const {
      page = 1,
      rowsPerPage = 10,
      searchKeyword = '',
      assignedTeacherId = '',
      isActive = '',
      roleId = '4' // Default to candidates
    } = params;
    
    let query = `page=${page}&rowsPerPage=${rowsPerPage}&roleId=${roleId}`;
    
    if (searchKeyword) {
      query += `&search_keyword=${encodeURIComponent(searchKeyword)}`;
    }
    
    if (assignedTeacherId) {
      query += `&assigned_teacher_id=${assignedTeacherId}`;
    }
    
    if (isActive !== '') {
      query += `&is_active=${isActive}`;
    }
    
    try {
      const response = await axios.get(`/users?${query}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }
  
  // Get user by ID
  static async getUserById(id) {
    try {
      const response = await axios.get(`/users/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user');
      }
      return response.data;
    } catch (error) {
      console.error(`Error fetching user ${id}:`, error.response?.data || error.message || error);
      throw error;
    }
  }
  
  // Create a new user
  static async createUser(userData) {
    try {
      const response = await axios.post('/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }
  
  // Update an existing user
  static async updateUser(id, userData) {
    try {
      const response = await axios.put(`/users/${id}`, userData);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update user');
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating user ${id}:`, error.response?.data || error.message || error);
      throw error;
    }
  }
  
  // Upload user documents
  static async uploadUserDocuments(userId, files) {
    try {
      const formData = new FormData();
      formData.append('id', userId);
      
      // Append all files to the form data
      for (const [key, file] of Object.entries(files)) {
        if (file instanceof File) {
          formData.append(key, file);
        }
      }
      
      const response = await axios.post('/user_document_upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading documents for user ${userId}:`, error);
      throw error;
    }
  }
  
  // Delete a user
  static async deleteUser(id) {
    try {
      const response = await axios.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting user ${id}:`, error);
      throw error;
    }
  }
  
  // Get master data for user forms
  static async getMasters() {
    try {
      const response = await axios.get('/users/masters');
      return response.data;
    } catch (error) {
      console.error('Error fetching user masters:', error);
      throw error;
    }
  }
}

export default UserService;